<template>
  <div class="party-selection">
    <div class="party-selection__parties">
      <div
        :class="partyClassList(i)"
        :data-id="i"
        ref="party"
        @click.self="onPartySelectedClick"
        v-for="(party, i) in editableParties" :key="i"
        data-ec="envelope_send"
        data-et="party_tab">
        <UserIcon
          height="18"
          width="18"
        />
        <span>{{ $t('createEnvelope.documentSetup.partySelection.partyNumber', { partyNumber: i + 1}) }}</span>
        <div
          class="party-selection__party__remove"
          :data-id="i"
          @click="removeParty"
          v-if="party.addedByUser">
          <XMarkIcon
            height="10"
            width="10"
          />
        </div>
      </div>
      <div class="party-selection__party party-selection__party--add" @click="onAddPartyClick">
        <PlusIcon
          height="18"
          width="18"
        />
      </div>
    </div>
    <div :class="contentClassList(i)" v-for="(party, i) in editableParties" :key="i" :data-test="`party-tab-${i}`">
      <h3 class="party-selection__content__header">
        <span>{{ $t('createEnvelope.documentSetup.partySelection.partyNumber', { partyNumber: i + 1 }) }} | </span>
        <a
            class="g-link"
            @click="addSelfAsParty"
            data-test="party-add-self"
            data-ec="envelope_send"
            data-et="add_myself_as_party_link">
          <UserPlusIcon />
          {{ $t('createEnvelope.documentSetup.partySelection.addMyselfAsParty') }}
        </a>
        <div class="party-selection__content__header__right">
          <b>{{ $t('createEnvelope.documentSetup.partySelection.receivesCopyOnly') }}</b>
          <VCheckbox
            type="checkbox"
            subtype="toggle"
            size="small"
            data-ec="envelope_send"
            data-et="receives_copy_only_toggle"
            :disabled="editableParties[i].cannotBeCopyOnly"
            :checked="editableParties[i].receivesCopyOnly"
            v-model="editableParties[i].receivesCopyOnly" />
        </div>
      </h3>
      <br />
      <VForm :data-vv-scope="`party-edit-${i}`">
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="6">
              <b>{{ $t('createEnvelope.documentSetup.partySelection.partyName') }}</b>
              <div class="party-selection__auto-complete" @focusout="autoCompleteLoseFocus">
                <VInput
                  type="text"
                  maxlength="100"
                  :class="{ 'a-input--error': errors.has(`party-edit-${i}.party-name`) }"
                  placeholder="e.g. Jo Bloggs"
                  v-validate="'required'"
                  data-vv-name="party-name"
                  :data-vv-as="$t('createEnvelope.documentSetup.partySelection.partyName')"
                  v-model="editableParties[i].title"
                  :data-vv-scope="`party-edit-${i}`"
                  data-test="input-party-title"
                  @input="autoCompleteSearch(editableParties[i].title)"
                  @updated="autoCompleteLoseFocus"
                  data-hj-suppress
                />
                <div class="party-selection__auto-complete__dropdown" v-if="contactsSearchResults && autoCompleteShow">
                  <ul class="party-selection__auto-complete__dropdown__list">
                    <li
                      v-for="(contact, index) in contactsSearchResults"
                      :key="index" tabindex="0"
                      @click="autoCompleteSelect(contact, i)"
                      @keydown.enter="autoCompleteSelect(contact, i)"
                      data-type="autoCompleteResult">
                      {{ contact.title }} ({{ contact.email }})
                    </li>
                  </ul>
                </div>
              </div>
              <VMessage
                type="error"
                subtype="basic"
                v-show="errors.has(`party-edit-${i}.party-name`)"
                class="party-selection__security-upgrade-message">
                <template v-slot:icon>
                  <XMarkIcon />
                </template>
                <template v-slot:text>
                  {{ errors.first(`party-edit-${i}.party-name`) }}
                </template>
              </VMessage>
            </div>
            <div slot="gridItem" type="6">
              <b>{{ $t('createEnvelope.documentSetup.partySelection.partyEmail') }}</b>
              <VInput
                type="text"
                :class="{ 'a-input--error': errors.has(`party-edit-${i}.party-email`) }"
                placeholder="e.g. jo@example.com"
                v-validate="'required|isTrimmedEmail'"
                data-vv-name="party-email"
                data-vv-as="party email"
                v-model.trim="editableParties[i].email"
                :data-vv-scope="`party-edit-${i}`"
                data-test="input-party-email"
                data-hj-suppress
              />
              <VMessage
                type="error"
                subtype="basic"
                v-show="errors.has(`party-edit-${i}.party-email`)">
                <template v-slot:icon>
                  <XMarkIcon />
                </template>
                <template v-slot:text>
                  {{ errors.first(`party-edit-${i}.party-email`) }}
                </template>
              </VMessage>
            </div>
          </VGrid>
          <a
            class="g-link spacing-top-xs"
            @click="toggleExtraInfo"
            data-ec="envelope_send"
            data-et="party_advanced_options"
          >
            <MinusSquareIcon v-if="showExtraInfo" />
            <PlusSquareIcon v-else />
            {{ $t('createEnvelope.documentSetup.partySelection.advancedOptions') }}
          </a>
          <div
            v-if="editableParties[i].showExtraInfo"
            :class="extraInfoClasslist"
          >
            <VGrid>
              <div slot="gridItem" type="6">
                <b>{{ $t('createEnvelope.documentSetup.partySelection.personalMessage') }}</b>
                <PersonalMessageTemplateSelector
                  @select="handleMessageTemplateSelect($event, party)"
                />
                <VInput
                  class="party-selection__content__extra__text-area"
                  type="textarea"
                  placeholder="Custom party message"
                  v-model="editableParties[i].message"
                  @input="handleMessageInput(party)"
                  data-hj-suppress
                  data-ec="envelope_send"
                  data-et="party_personal_message_textarea"
                />
                <BaseButton
                  :disabled="
                    !editableParties[i].message ||
                    !editableParties[i].messageDirty
                  "
                  variant="invisible"
                  class="float-right !p-0 mt-sm !no-underline !text-sm !text-primary disabled:!text-gray-400"
                  @click.native="openSaveMessageModal"
                  data-test="party-message-save"
                  data-ec="envelope_send"
                  data-et="party_personal_message_save"
                >
                  <SaveIcon />
                  {{ $t('createEnvelope.documentSetup.partySelection.saveNewMessageTemplate') }}
                </BaseButton>
                <VDynamicLoader
                  viewURL="modals/SaveUpdatePartyMessageModal"
                  :data="{
                    message: editableParties[i].message,
                    order: editableParties[i].order,
                  }"
                />
              </div>
              <div slot="gridItem" type="6">
                <b class="float-left">{{ $t('createEnvelope.documentSetup.partySelection.addPassword') }}</b>
                <VCheckbox
                  class="float-right"
                  type="checkbox"
                  subtype="toggle"
                  size="small"
                  :checked="editableParties[i].hasPassword"
                  v-model="editableParties[i].hasPassword"
                  @input="onPasswordToggle(i)"
                  data-ec="envelope_send"
                  data-et="party_password_toggle"
                />
                <div v-if="editableParties[i].hasPassword">
                  <VInput
                    class="party-selection__content__extra__password"
                    :class="{ 'a-input--error': errors.has(`party-edit-${i}.party-password`) }"
                    :data-vv-scope="`party-edit-${i}`"
                    :ref="`party-password-${i}`"
                    data-vv-name="party-password"
                    :data-vv-as="$t('createEnvelope.documentSetup.partySelection.partyPassword')"
                    v-validate="'max:20'"
                    type="text"
                    :placeholder="$t('createEnvelope.documentSetup.partySelection.password')"
                    v-model="editableParties[i].password"
                    @input="$validator.validateAll()"
                    data-hj-suppress
                  />
                  <VMessage
                    type="error"
                    subtype="basic"
                    v-show="errors.has(`party-edit-${i}.party-password`)">
                    <template v-slot:icon>
                      <XMarkIcon />
                    </template>
                    <template v-slot:text>
                      {{ errors.first(`party-edit-${i}.party-password`) }}
                    </template>
                  </VMessage>
                  <VInput
                    class="party-selection__content__extra__password-confirm"
                    :class="{ 'a-input--error': errors.has(`party-edit-${i}.party-password-confirm`) }"
                    :data-vv-scope="`party-edit-${i}`"
                    data-vv-name="party-password-confirm"
                    :data-vv-as="$t('createEnvelope.documentSetup.partySelection.partyPasswordConfirmation')"
                    v-validate="`required|confirmed:party-password-${i}`"
                    type="text"
                    :placeholder="$t('createEnvelope.documentSetup.partySelection.confirmPassword')"
                    v-model="confirmationPasswords[i]"
                    data-hj-suppress
                  />
                  <VMessage
                    type="error"
                    subtype="basic"
                    v-show="errors.has(`party-edit-${i}.party-password-confirm`)">
                    <template v-slot:icon>
                      <XMarkIcon />
                    </template>
                    <template v-slot:text>
                      {{ errors.first(`party-edit-${i}.party-password-confirm`) }}
                    </template>
                  </VMessage>
                </div>
              </div>
            </VGrid>
          </div>
        </div>
        <div slot="row" type="split" v-if="documentParties.length">
          <h3>{{ $t('createEnvelope.documentSetup.partySelection.templates') }}</h3>
          <VGrid>
            <div
              class="party-selection__template-party-selection"
              v-for="doc in documentsWithParties"
              slot="gridItem"
              type="6"
              :key="doc.fingerprint">
              <b data-hj-suppress>{{ doc.title }}</b>
              <VDropdown
                :list="[{title: $t('createEnvelope.documentSetup.partySelection.noParty')}, ...doc.parties]"
                :disabledItems="[false, ...doc.parties.map(party => assignedDocumentPartyIds.indexOf(party.id) > -1)]"
                :fluid="true"
                :defaultTitle="$t('createEnvelope.documentSetup.partySelection.noParty')"
                v-model="editableParties[i].selected"
                @onSelect="(party) => { onPartySelection(party, i, doc); }"
                data-ec="envelope_send"
                data-et="party_role_dropdown"
                data-hj-suppress
              />
            </div>
          </VGrid>
        </div>
      </VForm>
      <div
        class="party-selection__buttons"
        v-if="selected !== (editableParties.length - 1)">
        <VButton class="float-right" :action="nextParty" data-ec="send_envelope" data-et="party_next_party_button">
          <ArrowRightIcon />
          {{ $t('createEnvelope.documentSetup.partySelection.nextParty') }}
        </VButton>
      </div>
    </div>
  </div>
</template>

<script>
import VButton from '@/components/deprecated/atoms/Button.vue';
import VCheckbox from '@/components/deprecated/atoms/Checkbox.vue';
import VInput from '@/components/deprecated/atoms/Input.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import VDropdown from '@/components/deprecated/molecules/Dropdown.vue';
import VForm from '@/components/deprecated/organisms/Form.vue';
import VGrid from '@/components/deprecated/organisms/Grid.vue';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import PersonalMessageTemplateSelector from '@/components/ui/envelopes/PersonalMessageTemplateSelector.vue';
import VDynamicLoader from '@/components/DynamicLoader.vue';
import SearcherMixin from '@/mixins/searcherMixin';
import debounce from 'lodash/debounce';
import {
  ArrowRightIcon,
  PlusIcon,
  SaveIcon,
  UserIcon,
  UserPlusIcon,
  XMarkIcon,
  PlusSquareIcon,
  MinusSquareIcon,
} from '@/components/icons/fontIcons';

export default {
  name: 'VPartySelection',
  inject: {
    $validator: '$validator',
  },
  props: {
    documents: {
      type: Array,
      required: true,
    },
    parties: {
      type: Array,
      required: false,
    },
  },
  components: {
    VButton,
    VCheckbox,
    VInput,
    VMessage,
    VDropdown,
    VForm,
    VGrid,
    BaseButton,
    PersonalMessageTemplateSelector,
    VDynamicLoader,
    ArrowRightIcon,
    PlusIcon,
    SaveIcon,
    UserIcon,
    UserPlusIcon,
    XMarkIcon,
    PlusSquareIcon,
    MinusSquareIcon,
  },

  mixins: [SearcherMixin('contacts')],

  data() {
    return {
      selected: 0,
      arrowLeft: '20px',
      editableParties: [],
      confirmationPasswords: [],
      autoCompleteShow: false,
    };
  },
  created() {
    for (let i = 0; i < this.minParties; i += 1) {
      this.addParty({ addedByUser: false });

      // eslint-disable-next-line func-names
      this.autoCompleteSearch = debounce(async function (query) {
        if (query && query.length > 1) {
          // search methods provided by searcher mixins
          await this.searchContacts({ query });
          if (this.contactsSearchResults?.length) {
            this.autoCompleteShow = true;
          }
        } else {
          this.cancelContactsSearch();
          this.autoCompleteShow = false;
        }
      }, 300);
    }
  },
  updated() {
    this.$emit('input', {
      array: this.editableParties,
      selected: this.selected,
      isValid: this.isValid,
    });
    this.$store.commit('parties/SET_SELECTED_PARTIES', { parties: this.editableParties });
  },
  computed: {
    documentsWithParties() {
      return this.documents.filter(doc => doc.parties.length > 0);
    },
    documentPartiesSelection() {
      return this.documents.map(doc => ({
        disabled: this.assignedDocumentPartyIds.indexOf(doc.id) > -1,
        ...doc,
      }));
    },
    extraInfoClasslist() {
      const defaultClass = 'party-selection__content__extra';
      const activeClass = ` ${defaultClass}--active`;
      const isActive = this.editableParties[this.selected].showExtraInfo;
      return isActive ? (defaultClass + activeClass) : defaultClass;
    },
    showExtraInfo() {
      return this.editableParties[this.selected].showExtraInfo;
    },
    documentParties() {
      return this.documents.flatMap(doc => doc.parties);
    },
    minParties() {
      return this.documents
        .reduce((max, doc) => Math.max(doc.parties.length, max), 0)
          || 1;
    },
    assignedDocumentPartyIds() {
      return this.editableParties
        .flatMap(party => Object.values(party.template_party_ids));
    },
    allDocumentPartiesAreAssigned() {
      const unassignedDocuments = this.documentParties
        .filter(doc => this.assignedDocumentPartyIds.indexOf(doc.id) === -1);

      return unassignedDocuments.length === 0;
    },
    documentPartiesAreOnlyAssignedOnce() {
      return (new Set(this.assignedDocumentPartyIds)).size === this.assignedDocumentPartyIds.length;
    },
    envelopeHasTheMinimumNumberOfParties() {
      return this.editableParties.length >= this.minParties;
    },
    allPartiesHaveTheRequiredFields() {
      const invalidParties = this.editableParties
        .filter(party => !(party.email && party.title));

      return invalidParties.length === 0;
    },
    formValidationIsValid() {
      return this.errors.items.length === 0;
    },
    isValid() {
      return this.allDocumentPartiesAreAssigned
          && this.documentPartiesAreOnlyAssignedOnce
          && this.envelopeHasTheMinimumNumberOfParties
          && this.allPartiesHaveTheRequiredFields
          && this.formValidationIsValid;
    },
  },
  methods: {
    onAddPartyClick() {
      this.addParty();
      this.selectParty(this.editableParties.length - 1);
    },
    onPartySelectedClick($event) {
      const id = parseInt($event.target.getAttribute('data-id'), 10);
      this.selectParty(id);
    },
    partyIsValid(i) {
      if (this.errors.has(`party-edit-${i}.party-email`) || this.errors.has(`party-edit-${i}.party-name`)) return false;
      if (!this.editableParties[i].title || !this.editableParties[i].email) return false;
      return true;
    },
    handleMessageTemplateSelect(event, party) {
      // eslint-disable-next-line no-param-reassign
      party.message = event.text;
      // eslint-disable-next-line no-param-reassign
      party.messageDirty = false;
    },
    handleMessageInput(party) {
      // eslint-disable-next-line no-param-reassign
      party.messageDirty = true;
    },
    openSaveMessageModal() {
      this.$bus.$emit('toggleModal', { name: 'save-update-party-message-modal', active: true });
    },
    // Clicked add party from search selection
    autoCompleteSelect(contact, partyIndex) { // Select a contact from the party autocomplete
      this.editableParties[partyIndex].title = contact.title;
      this.editableParties[partyIndex].email = contact.email;
      this.autoCompleteShow = false;
      this.$nextTick(() => { // Allow DOM to update before we validate
        this.$validator.validateAll(`party-edit-${partyIndex}`); // Re validate after using info from contact
      });
    },
    autoCompleteLoseFocus() { // Wait to see if autocomplete component gets focus TODO Refactor use of setTimeout
      window.setTimeout(this.autoCompleteHide, 200);
    },
    autoCompleteHide() { // Hide autocomplete unless it has focus
      if (
        document.activeElement
        && document.activeElement.dataset
        && document.activeElement.dataset.type !== 'autoCompleteResult'
      ) {
        this.autoCompleteShow = false;
      }
    },
    onPartySelection(party, partyIndex, document) {
      return this.setMergeFields({ party, partyIndex, document });
    },
    partyClassList(index) {
      const defaultClass = 'party-selection__party';
      const selected = this.selected === index ? ` ${defaultClass}--selected` : '';
      const invalid = !this.partyIsValid(index) ? ` ${defaultClass}--bold` : '';
      return defaultClass + selected + invalid;
    },
    contentClassList(index) {
      const defaultClass = 'party-selection__content';
      const selected = this.selected === index ? ` ${defaultClass}--selected` : '';
      return defaultClass + selected;
    },
    selectParty(id) {
      this.selected = id;
      this.$emit('input', {
        array: this.editableParties,
        selected: this.selected,
        isValid: this.isValid,
      });
    },
    addParty(options) {
      this.editableParties.push(Object.assign({
        title: null,
        email: null,
        order: this.editableParties.length || 0,
        showExtraInfo: false,
        receivesCopyOnly: false,
        hasPassword: false,
        password: '',
        addedByUser: true,
        selected: {},
        mergeFields: {},
        message: '',
        template_party_ids: {},
      }, options));
    },
    removeParty($event) {
      const id = parseInt($event.target.getAttribute('data-id'), 10);
      if (this.selected >= (this.editableParties.length - 1)) {
        this.selected = 0;
      }
      this.editableParties.splice(id, 1);
      this.$emit('input', {
        array: this.editableParties,
        selected: this.selected,
        isValid: this.isValid,
      });
    },
    nextParty() {
      const nextSelected = this.selected + 1;
      if (nextSelected <= this.editableParties.length) {
        this.selected = nextSelected;
      }
    },
    getOldPartyId(fingerprint, selectedPartyIndex) {
      const documents = this.$store.getters['envelopes/creatingDocuments'];
      const doc = documents.filter(item => item.fingerprint === fingerprint)[0];
      let party = null;
      doc.parties.forEach((item, index) => {
        if (index === selectedPartyIndex) {
          party = item;
        }
      });
      return party || 0;
    },
    setMergeFields({
      party: { id: partyId },
      partyIndex: index,
      document: {
        contract_id: contractId,
        fingerprint: contractFingerprint,
      },
    }) {
      const mergeFields = this.$store.getters['envelopes/mergeFieldsForParty'](partyId);
      const fieldsForParty = this.$store.getters['envelopes/fieldsForParty'](partyId);

      if (partyId) {
        this.$set(this.editableParties[index].template_party_ids, contractId, partyId);
      } else {
        this.$delete(this.editableParties[index].template_party_ids, contractId);
      }

      // TODO Here we are setting properties on an array?! This is relied on later in PartySelectionForm?!
      mergeFields.contractId = contractId;
      this.$set(this.editableParties[index].mergeFields, contractFingerprint, mergeFields);

      this.editableParties[index].mergeFields[contractFingerprint].forEach((field) => {
        if (!field.content) {
          this.$set(field, 'content', '');
        }
      });

      if (fieldsForParty.length > 0) {
        this.editableParties[index].fields = fieldsForParty;
      }

      /*

      Determine if party can be set as copy only and whether or not it should be set:

      - If any of the roles selected for a party are signer roles, disable the copy-only toggle
      - If the only roles selected are copy roles, enable the copy-only toggle and set to true
      - If there are no roles assigned to the party then the toggle should be enabled but set to false

      */

      const allPartyRoleIdsForCurrentParty = Object.values(this.editableParties[index].template_party_ids);
      const fullRolesForParty = allPartyRoleIdsForCurrentParty.map(id => this.documentParties.find(party => party.id === id));
      const containsSignerRole = !!fullRolesForParty.find(role => role.role === 'signer');

      if (!containsSignerRole && !fullRolesForParty.length) {
        this.editableParties[index].receivesCopyOnly = false;
        this.editableParties[index].cannotBeCopyOnly = false;
      } else if (!containsSignerRole) {
        this.editableParties[index].receivesCopyOnly = true;
        this.editableParties[index].cannotBeCopyOnly = false;
      } else {
        this.editableParties[index].receivesCopyOnly = false;
        this.editableParties[index].cannotBeCopyOnly = true;
      }
    },
    mergeFieldsByParty(index) {
      const mergeFields = [];
      Object.values(this.editableParties[index].mergeFields)
        .forEach((field) => {
          mergeFields.push(field);
        });

      return mergeFields;
    },
    addSelfAsParty() { // Populates the current selected party with the users own data
      const user = this.$store.getters['user/user'];
      this.editableParties[this.selected].title = user.name;
      this.editableParties[this.selected].email = user.email;
      this.$nextTick(() => {
        this.$validator.validateAll(`party-edit-${this.selected}`);
      });
    },
    // Opens advanced options
    toggleExtraInfo() {
      this.editableParties[this.selected].showExtraInfo =
        !this.editableParties[this.selected].showExtraInfo;
    },
    onPasswordToggle(index) {
      const party = this.editableParties[index];
      if (!party.hasPassword) {
        party.password = '';
        this.confirmationPasswords[index] = '';
      }
    },
  },
};
</script>

<style scoped>
.party-selection__security-upgrade-message {
  margin-top: 0;
}

.party-selection__parties {
  border: 1px solid var(--color-border);
  border-bottom: 0;
  background: #fff;
  border-bottom: 3px solid var(--color-border);
}

.party-selection__parties:after {
  content: '';
  display: block;
  clear: both;
}

.party-selection__party {
  position: relative;
  padding: var(--spacing-base);
  margin-bottom: -3px;
  float: left;
  border-bottom: 3px solid transparent;
  cursor: pointer;
}

.party-selection__party:first-child {
  margin-left: 0;
}

.party-selection__party:hover {
  border-bottom: 3px solid var(--signable-secondary-2-shade-2);
}

.party-selection__party span {
  padding-left: 20px;
  pointer-events: none;
}

.party-selection__party svg {
  pointer-events: none;
  position: absolute;
  transform: translate3d(0, -50%, 0);
  top: 50%;
}

.party-selection__party--bold {
  font-weight: bold;
  font-style: italic;
}

.party-selection__party__remove {
  position: absolute;
  right: 0;
  top: 0;
  background: var(--color-error);
  border: 1px solid var(--color-error-dark);
  padding: 2px 2px;
  line-height: 0;
}

.party-selection__party__remove svg {
  position: initial;
  transform: initial;
  top: initial;
  left: initial;
  color: #fff;
  pointer-events: none;
}

.party-selection__party--selected {
  border-bottom: 3px solid var(--signable-primary-1);
}

.party-selection__party--selected:hover {
  border-bottom: 3px solid var(--signable-primary-1);
}

.party-selection__party--add {
  float: right;
  background: var(--signable-primary-1);
  height: 52px;
  width: 52px;
  border-bottom: 0;
}

.party-selection__party--add:hover {
  border-bottom: 0;
}

.party-selection__party--add svg {
  color: #fff;
  position: absolute;
  transform: translate3d(-50%, -50%, 0);
  top: 50%;
  left: 50%;
}

.party-selection__auto-complete {
  position: relative;
}

.party-selection__auto-complete .a-input {
  z-index: 5;
}

.party-selection__auto-complete__dropdown {
  position: absolute;
  top: 42px;
  width: 100%;
  z-index: 3;
}

.party-selection__auto-complete__dropdown__list {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #dde2e5;
  border-top-width: 0;
  border-bottom-left-radius: 3px;
  border-bottom-right-radius: 3px;
}

.party-selection__auto-complete__dropdown__list li {
  padding: 10px;
  background: var(--signable-secondary-4-shade-3);
  border-top: 1px solid #dde2e5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: 0.1s ease all;
  cursor: pointer;
}

.party-selection__auto-complete__dropdown__list li:nth-of-type(1) {
  border-top-width: 0;
}

.party-selection__auto-complete__dropdown__list li:nth-last-of-type(1) {
  border-bottom-left-radius: 2px;
  border-bottom-right-radius: 2px;
}

.party-selection__auto-complete__dropdown__list li:active, .party-selection__auto-complete__dropdown__list li:hover, .party-selection__auto-complete__dropdown__list li:focus {
  background: #fff;
}

.party-selection__content {
  padding: var(--spacing-base) var(--spacing-base) var(--spacing-large) var(--spacing-base);
  /* Extra bottom margin part of solution to sort out responsive button margins in PartySelectionForm */
  background: #fff;
  border: 1px solid var(--color-border);
  border-top: 0;
  display: none;
}

.party-selection__content:after {
  content: '';
  display: block;
  clear: both;
}

.party-selection__content__header *:not(span) {
  font-family: 'Roboto', sans-serif !important;
}

.party-selection__content__header a {
  font-size: 14px;
}

.party-selection__content__header__right {
  float: right;
  font-size: 12px;
}

.party-selection__content__header__right b {
  float: left;
  margin-right: var(--spacing-small);
  margin-top: 2px;
}

.party-selection__content__extra {
  height: 0;
  overflow: hidden;
  opacity: 0.1;
  transition: all .25s ease;
}

.party-selection__content__extra:after {
  content: '';
  display: block;
  clear: both;
}

.party-selection__content__extra--active {
  height: 100%;
  /*max-height: 300px;*/
  overflow: initial;
  opacity: 1;
  margin-top: var(--spacing-small);
}

.party-selection__content__extra__text-area {
  margin-top: var(--spacing-small);
  height: 215px;
}

.party-selection__content__extra__text-area ::v-deep textarea {
  resize: none;
  height: 100%;
}

.party-selection__content__extra__password-confirm {
  margin-top: var(--spacing-small);
}

.party-selection__content--selected {
  display: block;
}

.party-selection .party-selection__merge-fields__row {
  margin-top: var(--spacing-base);
}

.party-selection .party-selection__merge-fields__row:after {
  content: '';
  display: block;
  clear: both;
}

.party-selection .party-selection__merge-fields__row b {
  margin-top: 8px;
  margin-bottom: 0;
}

.party-selection__buttons {
  margin-top: var(--spacing-base);
  padding-top: var(--spacing-base);
  border-top: 1px solid var(--color-border);
}

@media screen and (max-width: 500px) {
  .party-selection__content__header__right {
    float: none;
    margin-top: var(--spacing-small);
  }
}

/* override combobox to fit page styles */
:deep(.combobox) {
  @apply text-current;
}
:deep(.combobox-placeholder) {
  @apply text-gray-400;
}
:deep(.combobox-wrapper),
:deep(.combobox-no-options),
:deep(.combobox-no-results),
:deep(.combobox-option) {
  @apply !px-sm;
}
</style>
